import type { Timesheet } from '@/types/timesheet';
import type { ApprovalDelegation, CreateDelegationRequest, UpdateDelegationRequest } from '@/components/DelegationManagement';
import { apiClient } from './apiClient';

export interface RecallTimesheetRequest {
  reason: string;
}

export interface ApproveTimesheetRequest {
  notes?: string;
}

export interface RejectTimesheetRequest {
  reason: string;
}

// Timesheet Approval Actions
export async function recallTimesheet(timesheetId: number, reason: string): Promise<Timesheet> {
  const data = await apiClient.post<{ timesheet: Timesheet }>(`/timesheets/${timesheetId}/recall`, { reason });
  return data.timesheet;
}

export async function approveTimesheet(timesheetId: number, notes?: string): Promise<Timesheet> {
  const data = await apiClient.post<{ timesheet: Timesheet }>(`/timesheets/${timesheetId}/approve`, { notes });
  return data.timesheet;
}

export async function rejectTimesheet(timesheetId: number, reason: string): Promise<Timesheet> {
  const data = await apiClient.post<{ timesheet: Timesheet }>(`/timesheets/${timesheetId}/reject`, { reason });
  return data.timesheet;
}

// Delegation Management
export async function createDelegation(
  managerId: string,
  request: CreateDelegationRequest
): Promise<ApprovalDelegation> {
  return await apiClient.post<ApprovalDelegation>('/delegations', {
    managerId,
    ...request,
  });
}

export async function getMyDelegations(): Promise<ApprovalDelegation[]> {
  return await apiClient.get<ApprovalDelegation[]>('/delegations/my-delegations');
}

export async function getDelegationsToMe(): Promise<ApprovalDelegation[]> {
  return await apiClient.get<ApprovalDelegation[]>('/delegations/delegated-to-me');
}

export async function updateDelegation(
  delegationId: string,
  updates: UpdateDelegationRequest
): Promise<void> {
  await apiClient.put<void>(`/delegations/${delegationId}`, updates);
}

export async function deactivateDelegation(delegationId: string): Promise<void> {
  await apiClient.post<void>(`/delegations/${delegationId}/deactivate`, {});
}

export async function getActiveDelegateForManager(
  managerId: string,
  forDate?: string
): Promise<string | null> {
  try {
    const endpoint = forDate
      ? `/delegations/manager/${managerId}/active-delegate?forDate=${encodeURIComponent(forDate)}`
      : `/delegations/manager/${managerId}/active-delegate`;
    const data = await apiClient.get<{ delegateId?: string }>(endpoint);
    return data.delegateId || null;
  } catch {
    return null;
  }
}

export async function canApproveForManager(
  userId: string,
  managerId: string
): Promise<boolean> {
  try {
    const data = await apiClient.get<{ canApprove?: boolean }>(
      `/delegations/can-approve?userId=${encodeURIComponent(userId)}&managerId=${encodeURIComponent(managerId)}`
    );
    return data.canApprove || false;
  } catch {
    return false;
  }
}

// Notification Management
export interface Notification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  actionUrl?: string;
  relatedEntityId?: string;
  relatedEntityType?: string;
  isRead: boolean;
  createdAt: Date;
  readAt?: Date;
  expiresAt?: Date;
}

export async function getNotifications(unreadOnly: boolean = false): Promise<Notification[]> {
  const endpoint = unreadOnly ? '/notifications?unreadOnly=true' : '/notifications';
  return await apiClient.get<Notification[]>(endpoint);
}

export async function getUnreadCount(): Promise<number> {
  try {
    const data = await apiClient.get<{ count?: number }>('/notifications/unread-count');
    return data.count || 0;
  } catch {
    return 0;
  }
}

export async function markNotificationAsRead(notificationId: string): Promise<void> {
  await apiClient.post<void>(`/notifications/${notificationId}/mark-read`, {});
}

export async function markAllNotificationsAsRead(): Promise<void> {
  await apiClient.post<void>('/notifications/mark-all-read', {});
}
